import React, { useState, useMemo, useCallback } from 'react';
import { Button, Dialog, DialogContent, withStyles, withTheme } from '@material-ui/core';
import { compose } from 'recompose';
import { withSnackbar } from 'notistack';
import {
  Header,
  DetailsSection,
  KPISection,
  MovementSummary
} from './components';
import GridsContent from './components/GridsContent';
import { useRouteProMapping } from './hooks';
import { getFilteredShipments } from './utils';
import { styles } from './styles';

interface ShipmentMovementProps {
  classes: Record<string, string>;
  theme: any;
  open: boolean;
  onClose: () => void;
  rowData: any;
}

interface MovementSummaryState {
  isOpen: boolean;
  proId: string | null;
}

const ShipmentMovement: React.FC<ShipmentMovementProps> = ({
  classes,
  theme,
  open,
  onClose,
  rowData,
}) => {
  const [selectedRoutes, setSelectedRoutes] = useState<any[]>([]);
  const [movementSummaryState, setMovementSummaryState] = useState<MovementSummaryState>({
    isOpen: false,
    proId: null
  });
  const [maximizedGridType, setMaximizedGridType] = useState<string | null>(null);

  const planDate = rowData?.date;
  const terminalCode = rowData?.terminalCode;
  const scenarioId = rowData?.scenarioId;

  const isHookEnabled = Boolean(open && planDate && terminalCode && scenarioId);

  const {
    routes,
    allShipments,
    loading,
    error,
    gridStatus
  } = useRouteProMapping({
    planDate,
    terminalCode,
    scenarioId,
    enabled: isHookEnabled
  });

  const filteredShipments = useMemo(() => {
    return getFilteredShipments({
      allShipments,
      selectedRoutes
    });
  }, [allShipments, selectedRoutes]);

  const handleMovementSummaryToggle = useCallback((event?: any) => {
    const shipmentData = event?.data;
    if (shipmentData?.proId) {
      setMovementSummaryState({ isOpen: true, proId: shipmentData.proId });
    } else {
      setMovementSummaryState({ isOpen: false, proId: null });
    }
  }, []);

  const handleClose = useCallback(() => {
    setSelectedRoutes([]);
    setMaximizedGridType(null);
    setMovementSummaryState({ isOpen: false, proId: null });
    onClose();
  }, [onClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        classes={{ paper: classes.dialogContainer }}
        className={theme.themeName}
        aria-label="Shipment Movement"
      >
        <Header onClose={handleClose} />
        <DialogContent className={classes.dialogContent}>
          <DetailsSection rowData={rowData} />
          <KPISection rowData={rowData} />
          <GridsContent
            routes={routes}
            filteredShipments={filteredShipments}
            gridStatus={gridStatus}
            loading={loading}
            error={error}
            maximizedGridType={maximizedGridType}
            onRoutesSelected={setSelectedRoutes}
            onShipmentDoubleClick={handleMovementSummaryToggle}
            onToggleGridMaximize={setMaximizedGridType}
          />
        </DialogContent>
        <div className={classes.dialogFooter}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            className={classes.closeButtonFooter}
          >
            Close
          </Button>
        </div>
      </Dialog>

      <MovementSummary
        open={movementSummaryState.isOpen}
        onClose={handleMovementSummaryToggle}
        proId={movementSummaryState.proId}
        planDate={planDate}
        terminalCode={terminalCode}
        scenarioId={scenarioId}
      />
    </>
  );
};

export default compose(
  withTheme(),
  withStyles(styles),
  withSnackbar
)(ShipmentMovement);
